import React, { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { enquiryAPI } from "../services/api";
import { Helmet } from "react-helmet-async";
import Loader from "../components/Loader";
import "./ConsultationEnquiriesPage.css";

export default function ConsultationEnquiriesPage() {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [showMessageModal, setShowMessageModal] = useState(false);

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const fetchEnquiries = async () => {
        try {
            const response = await enquiryAPI.getAll();
            if (response.data.success) {
                setEnquiries(response.data.data);
            } else {
                setError("Failed to fetch enquiries");
            }
        } catch (err) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleViewMessage = async (enquiry) => {
        setSelectedEnquiry(enquiry);
        setShowMessageModal(true);

        if (!enquiry.is_viewed) {
            try {
                const response = await enquiryAPI.markAsViewed(enquiry.id);
                if (response.data.success) {
                    setEnquiries(prev => prev.map(e =>
                        e.id === enquiry.id ? response.data.data : e
                    ));
                }
            } catch (err) {
                console.error("Failed to mark as viewed", err);
            }
        }
    };

    const closeMessageModal = () => {
        setShowMessageModal(false);
        setSelectedEnquiry(null);
    };

    return (
        <DashboardLayout activeTab="enquiries">
            <Helmet>
                <title>Consultation Enquiries - Trainer Dashboard</title>
            </Helmet>

            <div className="enquiries-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-chat-left-text-fill me-2"></i>
                            Consultation Enquiries
                        </h3>
                        <p className="text-muted">Manage your consultation requests from potential clients</p>
                    </div>
                </div>

                {/* Enquiries List */}
                <div className="form-section">
                    <h5 className="section-title">
                        <i className="bi bi-list-ul me-2"></i>
                        Recent Enquiries
                    </h5>

                    {error && <div className="alert alert-danger">{error}</div>}

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th>Client Name</th>
                                    <th>Contact Info</th>
                                    <th>Service Interest</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-danger" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="text-muted mt-2">Loading enquiries...</p>
                                        </td>
                                    </tr>
                                ) : enquiries.length > 0 ? (
                                    enquiries.map((enquiry) => (
                                        <tr key={enquiry.id} style={{ backgroundColor: !enquiry.is_viewed ? '#f8f9fa' : 'transparent' }}>
                                            <td className="text-nowrap text-muted">
                                                <small>{formatDate(enquiry.created_at)}</small>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar-circle-sm bg-light text-primary me-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%', fontWeight: 'bold' }}>
                                                        {enquiry.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="fw-medium">{enquiry.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <small className="mb-1"><i className="bi bi-envelope me-1"></i> {enquiry.email}</small>
                                                    {/* <small><i className="bi bi-telephone me-1"></i> {enquiry.phone}</small> */}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="badge bg-soft-primary text-primary border border-primary-subtle rounded-pill px-3">
                                                    {enquiry.service || "General Enquiry"}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className={`btn btn-sm ${enquiry.is_viewed ? "btn-danger" : "btn-outline-danger"}`}
                                                    title={enquiry.is_viewed ? "Actually Viewed" : "View Details"}
                                                    onClick={() => handleViewMessage(enquiry)}
                                                >
                                                    {enquiry.is_viewed ? "Viewed" : "View"}
                                                </button>
                                                {/* <a href={`mailto:${enquiry.email}`} className="btn btn-sm btn-outline-secondary ms-1" title="Reply via Email">
                                                    <i className="bi bi-reply"></i>
                                                </a> */}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="empty-state text-center">
                                                <div className="mb-3">
                                                    <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem", opacity: 0.5 }}></i>
                                                </div>
                                                <h5 className="text-muted">No enquiries found</h5>
                                                <p className="text-muted small">When potential clients contact you, they will appear here.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Message Modal */}
            {showMessageModal && selectedEnquiry && (
                <div className="modal-overlay" onClick={closeMessageModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5>
                                <i className="bi bi-chat-text-fill me-2"></i>
                                Enquiry Details
                            </h5>
                            <button type="button" className="btn-close" onClick={closeMessageModal}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="avatar-circle-sm bg-light text-primary me-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', fontSize: '18px' }}>
                                        {selectedEnquiry.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h5 className="mb-0">{selectedEnquiry.name}</h5>
                                        <small className="text-muted">{formatDate(selectedEnquiry.created_at)}</small>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="text-muted small">Email</label>
                                        <div className="fw-medium"><i className="bi bi-envelope me-2"></i>{selectedEnquiry.email}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="text-muted small">Phone</label>
                                        <div className="fw-medium"><i className="bi bi-telephone me-2"></i>{selectedEnquiry.phone}</div>
                                    </div>
                                    <div className="col-12">
                                        <label className="text-muted small">Service Interest</label>
                                        <div>
                                            <span className="badge bg-soft-primary text-primary border border-primary-subtle rounded-pill px-3">
                                                {selectedEnquiry.service || "General Enquiry"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="message-box p-3 bg-light rounded border">
                                <label className="text-muted small mb-2 d-block text-uppercase fw-bold">Message</label>
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedEnquiry.message || "No message provided."}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeMessageModal}>Close</button>
                            <a href={`mailto:${selectedEnquiry.email}`} className="btn btn-primary">
                                <i className="bi bi-reply-fill me-2"></i>Reply via Email
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
